import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { QuestionnairesService } from 'ph-bhp-feature-packages/ph-bhp-feature-package-check/lib/services/questionnaires.service';
import { PHBHPLayoutPackageService } from 'ph-bhp-feature-packages/ph-bhp-layout-package/public_api';
import { TranslationKeys } from 'ph-bhp-localization';
import { Subscription } from 'rxjs';
import { AppConfig } from 'src/app/app.config';

@Component({
  selector: 'hygiene-check-editor',
  templateUrl: './hygiene-check-editor.component.html',
  styleUrls: ['./hygiene-check-editor.component.css']
})
export class HygieneCheckEditorComponent implements OnInit, OnDestroy {

  public pageInfo: any;
  public fields: any[];
  public gridData: any[];
  public editDataItem: any;
  public deleteDataItem: any;
  public deleteRowIndex: any;
  public modalRef: BsModalRef;
  public defaultQuestionnaireID = null;
  public hasChecksInProgress: boolean = false;
  public userId: number;
  public roleId: number;
  public countryId: number;
  readonly questionnaireColumnFieldName: string = 'questionnaireName';
  readonly questionsColumnFieldName: string = 'questionsNumber';
  readonly createdByFieldName: string = 'createdByName';
  readonly defaultQuestionnaireColumnFieldName: string = 'defaultQuestionnaire';

  @ViewChild('deleteModal') deleteModal: TemplateRef<any>;
  @ViewChild('editModal') editModal: TemplateRef<any>;

  translationKeys: typeof TranslationKeys = TranslationKeys;

  private institutionSubscription: Subscription;

  public actionItems: any = [
    { name: 'CHECK.CONFIGURE.VIEW', imgSrc: 'assets/ph-bhp-layout/svg/icon-view.svg', isEdit: true, onClick: (dataItem) => { this.previewQuestionnaire(dataItem) } },
    { name: 'CHECK.CONFIGURE.EDIT', imgSrc: 'assets/ph-bhp-layout/svg/icon-edit.svg', isEdit: true, onClick: (dataItem) => { this.editQuestionnaire(dataItem) } },
    { name: 'CHECK.CONFIGURE.DUPLICATE', imgSrc: 'assets/ph-bhp-layout/svg/icon-duplicate.svg', isEdit: true, onClick: (dataItem) => { this.duplicateQuestionnaire(dataItem); } },
    { name: 'CHECK.CONFIGURE.DELETE', imgSrc: 'assets/ph-bhp-layout/svg/icon-delete.svg', onClick: (dataItem) => { this.deleteQuestionnaire(dataItem) } }
  ];

  public customButton: any;

  constructor(
    private modalService: BsModalService,
    private questionnaireService: QuestionnairesService,
    private config: AppConfig,
    public router: Router,
    public translate: TranslateService,
    public layoutService: PHBHPLayoutPackageService,
  ) { }

  ngOnInit(): void {

    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      let mode = navigation?.extras?.state;
      const newQuestion = navigation.extras.state.newQuestion;
    }

    this.setupUserData();
    this.setupGridTable();
    this.institutionSubscription = this.layoutService.institutionIDSubject.asObservable().subscribe(() => {
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    if (this.institutionSubscription) {
      this.institutionSubscription.unsubscribe();
    }
  }

  private setupGridTable(): void {
    this.fields = [
      { 'field': this.questionnaireColumnFieldName, 'title': 'CHECK.CONFIGURE.QUESTIONNAIRE' },
      { 'field': this.questionsColumnFieldName, 'title': 'CHECK.CONFIGURE.QUESTIONS' },
      { 'field': this.createdByFieldName, 'title': 'CHECK.CONFIGURE.CREATED_BY' },
      {
        'field': this.defaultQuestionnaireColumnFieldName, 'title': 'CHECK.CONFIGURE.DEFAULT_QUESTIONNAIRE',
        editTemplate: {
          type: 'radio',
          textField: "",
          validText: 'CHECK.CONFIGURE.ACTIVE',
          invalidText: 'CHECK.CONFIGURE.INACTIVE',
          valueField: 'id',
          isChecked: (questionnaire: any) => {
            return questionnaire.isActive
          }
        }
      }
    ];

    this.pageInfo = {
      items: 'CHECK.CONFIGURE.QUESTIONNAIRES',
      itemsPerPageLabel: 'CHECK.CONFIGURE.QUESTIONNAIRES_PER_PAGE'
    };

    this.customButton = {
      onClick: (): void => { this.navigateToQuestionnaireEditor("new"); },
      addImage: true,
      buttonText: 'CHECK.CONFIGURE.ADD_NEW_QUESTIONNAIRE',
      isActive: true,
      customClass: 'btn btn-continue-bordered-bd'
    };
  }

  private setupUserData(): void {
    this.userId = this.config.userData.userId;
    this.roleId = this.config.userData.roleName.roleId;
    this.countryId = parseInt(this.config.userData.country);
  }

  private loadData(): void {
    this.layoutService.showLoader = true;
    var entityId = this.roleId == 2 ? this.countryId : this.layoutService.institutionID;
    if (!entityId) return;
    this.questionnaireService.getQuestionnairesListForEditor(this.roleId, entityId).subscribe((response: any) => {

      if(response?.length === 1 && !response[0].defaultQuestionnaire) {
        this.layoutService.showLoader = true
        this.defaultQuestionnaireID = response[0].questionnaireID;
        this.questionnaireService.putDefaultQuestionnaireForEntity({
          defaultQuestionnaireId: this.defaultQuestionnaireID,
          questionnaireIds: response.map(questionnaire => questionnaire.questionnaireID),
          userId: this.userId
        }).subscribe(() => {
          this.loadData();
        }, (error) => {
          console.error(error);
          this.layoutService.showLoader = false;
        }, () => {
          this.layoutService.showLoader = false;
        });
      }

      for (let i = 0; i < response.length; i++) { response[i].id = response[i].questionnaireID; }
      response = response.sort((a, b) => a.questionnaireName.localeCompare(b.questionnaireName));
      response = response.sort((a, b) => (b.defaultQuestionnaire ? 1 : 0) - (a.defaultQuestionnaire ? 1 : 0));
      this.gridData = response;
      const defaultQuestionnaire = this.gridData.find(item => item.defaultQuestionnaire);
      this.defaultQuestionnaireID = defaultQuestionnaire ? defaultQuestionnaire.questionnaireID : null;
    }, (error) => {
      console.error('Error loading questionnaires', error);
      this.layoutService.showLoader = false;
    }, () => {
      this.layoutService.showLoader = false;
    });
  }

  handlerData(event: any) {
    if (event && event.type && event.type === 'default') {
      this.defaultQuestionnaireID = event.defaultId;
      this.layoutService.showLoader = true;
      this.questionnaireService.putDefaultQuestionnaireForEntity({
        defaultQuestionnaireId: this.defaultQuestionnaireID,
        questionnaireIds: this.gridData.map(questionnaire => questionnaire.questionnaireID),
        userId: this.userId
      }).subscribe(() => {
        this.loadData();
      }, (error) => {
        console.error(error);
        this.layoutService.showLoader = false;
      }, () => {
        this.layoutService.showLoader = false;
      });
    }
  }

  addNew() {

  }

  // View
  previewQuestionnaire(dataItem) {
    this.router.navigate(["/questionnaire-viewer/" + dataItem.questionnaireID]);
  }
  // Edit
  editQuestionnaire(dataItem) {
    if (this.roleId === 2) {
      this.navigateToQuestionnaireEditor(dataItem.questionnaireID);
      return;
    }

    this.layoutService.showLoader = true;
    this.questionnaireService.hasChecksInProgressForQuestionnaire(dataItem.questionnaireID).subscribe((response: any) => {
      if (response) {
        this.openEditModal(this.editModal, dataItem);
      } else {
        this.navigateToQuestionnaireEditor(dataItem.questionnaireID);
      }
    }, (error) => {
      console.error(error);
      this.layoutService.showLoader = false;
    },
      () => {
        this.layoutService.showLoader = false;
      });
  }

  openEditModal(editModal: TemplateRef<any>, dataItem, rowIndex?) {
    this.editDataItem = dataItem;
    this.modalRef = this.modalService.show(editModal, Object.assign({}, { class: 'w-420' }));
  }

  navigateToQuestionnaireEditor(questionnaireId, fromDuplicate: boolean = false) {
    this.questionnaireService.editQuestionnaire = null;
    this.questionnaireService.fromDuplicate = fromDuplicate;
    this.router.navigate([`/questionnaire-editor/${questionnaireId}`]);
  }

  // Duplicate
  duplicateQuestionnaire(dataItem) {
    this.layoutService.showLoader = true;
    const data = {
      questionnaireID: dataItem.questionnaireID,
      prefix: this.layoutService.translateText("CHECK.CONFIGURE.COPY_OF"),
      userId: this.config.userData.userId,
    }
    this.questionnaireService.duplicateQuestionnaire(data).subscribe((response: any) => {
      this.layoutService.showLoader = false;
      this.navigateToQuestionnaireEditor(response.id, this.roleId === 2);
    }, (error) => {
      console.error(error);
      this.layoutService.showLoader = false;
    }, () => {
      this.layoutService.showLoader = false;
    });
  }

  // Delete
  deleteQuestionnaire(dataItem) {
    if (this.roleId === 2) {
      this.openDeleteModal(this.deleteModal, dataItem);
      return;
    }

    this.layoutService.showLoader = true;
    this.questionnaireService.hasChecksInProgressForQuestionnaire(dataItem.questionnaireID).subscribe((response: any) => {
      this.hasChecksInProgress = response;
      this.openDeleteModal(this.deleteModal, dataItem);
    }, (error) => {
      console.error(error);
      this.layoutService.showLoader = false;
    }, () => {
      this.layoutService.showLoader = false;
    });
  }

  openDeleteModal(deleteModal: TemplateRef<any>, dataItem, rowIndex?) {
    this.deleteDataItem = dataItem;
    this.deleteRowIndex = rowIndex ? rowIndex : null;
    this.modalRef = this.modalService.show(deleteModal, Object.assign({}, { class: 'w-420' }));
  }

  deleteQuestionnaireConfirmed(dataItem) {
    var userId = this.config.userData.userId;
    this.layoutService.showLoader = true;
    this.questionnaireService.deleteQuestionnaire(dataItem.questionnaireID, userId).subscribe(() => {
      this.loadData();
    }, (error) => {
      console.error(error);
      this.layoutService.showLoader = false;
    }, () => {
      this.layoutService.showLoader = false;
    });
  }
}
